import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getPortfolioSummary } from "@/lib/api";
import { cn } from "@/lib/utils";

const fmtMoney = (v) =>
  `${v < 0 ? "-" : "+"}$${Math.abs(v).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

/* Cifra a rullo: ogni carattere scivola quando cambia (stile odometro). */
const Odometer = ({ text, className, testid }) => (
  <span data-testid={testid} className={cn("inline-flex overflow-hidden", className)}>
    <AnimatePresence mode="popLayout" initial={false}>
      {String(text).split("").map((ch, i) => (
        <motion.span
          key={`${i}-${ch}`}
          initial={{ y: 26, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -26, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="inline-block tabular-nums"
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </AnimatePresence>
  </span>
);

const Spark = ({ series }) => {
  if (!series || series.length < 3) return null;
  const W = 140, H = 36;
  const cums = series.map((p) => p.cum);
  const lo = Math.min(...cums, 0), hi = Math.max(...cums, 1);
  const X = (i) => (i / (series.length - 1)) * W;
  const Y = (v) => H - 2 - ((v - lo) / (hi - lo || 1)) * (H - 4);
  const d = series.map((p, i) => `${i === 0 ? "M" : "L"}${X(i).toFixed(1)},${Y(p.cum).toFixed(1)}`).join(" ");
  const up = cums[cums.length - 1] >= 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-[140px] h-[36px]">
      <path d={d} fill="none" stroke={up ? "#10B981" : "#F43F5E"} strokeWidth="1.5" />
    </svg>
  );
};

export const PulseTicker = ({ pulse }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [mi, setMi] = useState(0);
  const [clock, setClock] = useState("");

  useEffect(() => {
    getPortfolioSummary().then(setPortfolio).catch(() => {});
    const p = setInterval(() => getPortfolioSummary().then(setPortfolio).catch(() => {}), 30000);
    return () => clearInterval(p);
  }, []);
  useEffect(() => {
    const c = setInterval(() =>
      setClock(new Date().toISOString().slice(11, 19) + " UTC"), 1000);
    return () => clearInterval(c);
  }, []);
  useEffect(() => {
    const r = setInterval(() => setMi((m) => m + 1), 5000);
    return () => clearInterval(r);
  }, []);

  const metrics = useMemo(() => {
    const out = [];
    const trades = pulse?.trades || [];
    if (pulse?.totalPnl != null) {
      out.push({ label: "P&L reale · ultimi 30 giorni", value: fmtMoney(pulse.totalPnl), good: pulse.totalPnl >= 0 });
    }
    const reals = portfolio?.reals || {};
    const cap = Object.values(reals).filter((r) => r?.ready && r.equity != null)
      .reduce((a, r) => a + Number(r.equity), 0);
    if (cap > 0) out.push({ label: "Capitale reale in gestione", value: `$${cap.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`, good: true, neutral: true });
    if (trades.length) {
      const wins = trades.filter((t) => t.pnl > 0).length;
      out.push({ label: `Win-rate · ${trades.length} trade chiusi`, value: `${Math.round((wins / trades.length) * 100)}%`, good: wins / trades.length >= 0.5 });
      const best = trades.reduce((a, t) => (t.pnl > a.pnl ? t : a), trades[0]);
      out.push({ label: `Miglior trade · ${best.market || best.platform}`, value: fmtMoney(best.pnl), good: best.pnl > 0 });
    }
    return out;
  }, [pulse, portfolio]);

  const dateStr = useMemo(() =>
    new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }), []);

  if (!metrics.length) return null;
  const m = metrics[mi % metrics.length];

  return (
    <div data-testid="hub-ticker" className="flex w-full flex-col items-center text-center">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground/90">{dateStr}</p>
      <AnimatePresence mode="wait">
        <motion.p key={m.label} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="mt-6 text-[11px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
          {m.label}
        </motion.p>
      </AnimatePresence>
      <Odometer text={m.value} testid="hub-ticker-value"
        className={cn("mt-2 font-mono text-6xl md:text-8xl font-semibold tracking-tighter leading-none",
          m.neutral ? "text-foreground lum-white" : m.good ? "text-emerald-400 lum" : "text-rose-400 lum-rose")} />
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2 font-mono text-[11px] text-muted-foreground">
        {Object.entries(pulse?.platforms || {}).map(([k, v]) => (
          <span key={k} className="rounded-md border border-border px-2.5 py-1">
            {k} <span className={cn("tabular-nums ml-1", v >= 0 ? "text-emerald-400" : "text-rose-400")}>{fmtMoney(v)}</span>
          </span>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-4">
        <Spark series={pulse?.series} />
        <p className="font-mono text-xs tabular-nums text-muted-foreground" data-testid="hub-ticker-clock">{clock}</p>
        <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-500">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
        </p>
      </div>
    </div>
  );
};

export default PulseTicker;
